import * as React from "react";
// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import { ContextualMenuItemType } from "@fluentui/react";
// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { IContextualMenuItem } from "@fluentui/react";
import type { MenuDividerProps } from "@fluentui/react-components";
import { AppTooltipContainer, useV9TooltipEnabled } from "@1js/acui-tooltip";
import {
  MenuDivider,
  MenuGroup,
  MenuGroupHeader,
  useId,
  Tooltip,
} from "@fluentui/react-components";

import {
  shimMenuItemProps,
  shimMenuHeaderProps,
  shimMenuTitleProps,
} from "./shimMenuProps";
import type { FooterItemProps } from "../AppMenuItem";
import { AppMenuItem } from "../AppMenuItem";
import { useAppMenuItemCustomStyles } from "./useAppMenuItemStyles";
import { SubMenu } from "./subMenu";
import { CustomMenuItem } from "./CustomMenuItem";
import type { ShortcutMetadata } from "./ShortcutPills.types";

export type MenuItemShimProps = {
  itemProps: IContextualMenuItem;
  onDismiss?: (
    ev?: Event | React.MouseEvent | React.KeyboardEvent,
    dismissAll?: boolean
  ) => void;
  shortcutMetadata?: ShortcutMetadata;
  footerItem?: FooterItemProps;
};

export const MenuItemShim = (menuItemShimProps: MenuItemShimProps) => {
  const props = menuItemShimProps.itemProps;
  const onDismiss = menuItemShimProps.onDismiss;
  const itemStyles = useAppMenuItemCustomStyles();

  const menuItemSectionId = useId("menu-item-section");

  const footerItemProps = menuItemShimProps.footerItem;
  const footerItem: FooterItemProps = {
    isFooterItem: footerItemProps?.isFooterItem || false,
    footerItemIndex: footerItemProps?.footerItemIndex ?? 0,
    footerItemCount: footerItemProps?.footerItemCount ?? 0,
  };

  // props.data can contain any custom data that the caller wants to pass to the menu item.
  // We should not assume that any item with data is a custom item, but given that we don't
  // want regression, we are limiting the check of persistOnClick to only
  // work for native Fluent UI menu items.
  const persistOnClickIsPresent = props.data && "persistOnClick" in props.data;

  if (props.data && !persistOnClickIsPresent) {
    // For items that have custom rendering, we pass their onRender() in V9 Menu Item.
    return <CustomMenuItem itemProps={props} onDismiss={onDismiss} />;
  }

  if (props.itemType === ContextualMenuItemType.Header) {
    const shimHeaderText = shimMenuTitleProps(props);
    const sectionHeader = shimHeaderText ? (
      <MenuGroupHeader className={itemStyles.menuGroupHeader}>
        {shimHeaderText}
      </MenuGroupHeader>
    ) : null;
    return sectionHeader;
  }

  if (props.itemType === ContextualMenuItemType.Divider) {
    const shimProps = shimMenuItemProps(props);
    return (
      <MenuDivider
        className={itemStyles.divider}
        {...(shimProps as MenuDividerProps)}
      />
    );
  }

  if (props.itemType === ContextualMenuItemType.Section) {
    const sectionProps = props.sectionProps;
    const shimHeaderText = shimMenuHeaderProps(props);

    const itemCount = sectionProps?.items?.length || 0;
    const sectionTopDivider = sectionProps?.topDivider ? (
      <MenuDivider className={itemStyles.divider} />
    ) : null;
    const sectionBottomDivider = sectionProps?.bottomDivider ? (
      <MenuDivider className={itemStyles.divider} />
    ) : null;
    const sectionHeader =
      typeof sectionProps?.title === "object" && sectionProps.title?.data ? (
        <MenuItemShim
          key={`${menuItemSectionId}-header`}
          itemProps={sectionProps.title}
          onDismiss={onDismiss}
          shortcutMetadata={menuItemShimProps.shortcutMetadata}
        />
      ) : shimHeaderText ? (
        <MenuGroupHeader className={itemStyles.menuGroupHeader}>
          {shimHeaderText}
        </MenuGroupHeader>
      ) : null;
    const sectionItems = (
      <MenuGroup>
        {sectionHeader}
        {sectionProps?.items.map((item, index) => {
          const footerItemPropInSection = footerItemProps
            ? {
                footerItemIndex: index,
                footerItemCount: itemCount,
                isFooterItem: footerItemProps.isFooterItem,
              }
            : undefined;
          return (
            <MenuItemShim
              key={`${menuItemSectionId}-${index}`}
              itemProps={item}
              footerItem={footerItemPropInSection}
              onDismiss={onDismiss}
              shortcutMetadata={menuItemShimProps.shortcutMetadata}
            />
          );
        })}
      </MenuGroup>
    );
    return (
      <>
        {sectionTopDivider}
        {sectionItems}
        {sectionBottomDivider}
      </>
    );
  }

  // Handling render of a sub-menu or a menu split item.
  if (props.subMenuProps) {
    return (
      <SubMenu
        itemProps={props}
        onDismiss={onDismiss}
        shortcutMetadata={menuItemShimProps.shortcutMetadata}
      />
    );
  }

  const shimProps = shimMenuItemProps(props);
  if (props.ariaDescription) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const v9TooltipEnabled = useV9TooltipEnabled();
    const renderAppMenuItem = (
      <AppMenuItem
        className={props.checked ? itemStyles.checked : undefined}
        footerItem={footerItem}
        {...shimProps}
        shortcutMetadata={menuItemShimProps.shortcutMetadata}
      />
    );
    // For menu items that have ariaDescription prop set, we will show Tooltip.
    return v9TooltipEnabled ? (
      <AppTooltipContainer
        label={props.ariaDescription}
        customTooltip={props.ariaDescription}
        showTooltip={true}
        directionalHint={9}
      >
        {renderAppMenuItem}
      </AppTooltipContainer>
    ) : (
      <Tooltip
        content={props.ariaDescription}
        relationship={"description"}
        positioning={"before"}
        withArrow={true}
      >
        {renderAppMenuItem}
      </Tooltip>
    );
  }

  return (
    <AppMenuItem
      className={props.checked ? itemStyles.checked : undefined}
      footerItem={footerItem}
      {...shimProps}
      shortcutMetadata={menuItemShimProps.shortcutMetadata}
    />
  );
};
